import React, {useEffect} from 'react';
import {useForm, Controller, useWatch} from 'react-hook-form';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import {IFdFarm} from 'constants/interfaces';

/* ---------- helper ---------- */
const toDateInputValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'string') return raw.split('T')[0];
  const num = Number(raw);
  if (Number.isNaN(num)) return '';

  // Handle both Unix timestamps and modified epoch days
  let date: Date;
  if (num > 1e12) {
    // Milliseconds timestamp
    date = new Date(num);
  } else if (num > 1e9) {
    // Unix timestamp (seconds since 1970)
    date = new Date(num * 1000);
  } else {
    // Assume it's days since modified epoch (2000-01-01)
    // This handles dates beyond 2038 that were encoded using our workaround
    const MODIFIED_EPOCH = new Date('2000-01-01T00:00:00Z').getTime();
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    date = new Date(MODIFIED_EPOCH + num * MS_PER_DAY);
  }

  return date.toISOString().slice(0, 10);
};

/* ---------- Year 2038 Problem workaround ---------- */
// Modified epoch: January 1, 2000 (to avoid Year 2038 Problem)
// TODO: Long-term solution should involve backend schema migration to:
//   1. Use BIGINT instead of INT for leaseEndDate column, OR
//   2. Use DATE type instead of numeric timestamp, OR
//   3. Implement this encoding consistently across the entire system
const MODIFIED_EPOCH = new Date('2000-01-01T00:00:00Z').getTime();
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Convert date string to days since modified epoch (Jan 1, 2000)
 * This avoids the Year 2038 Problem by using a more recent epoch
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @return {number} Number of days since January 1, 2000
 */
function dateToModifiedDays(dateString: string): number {
  const date = new Date(dateString + 'T00:00:00Z'); // Ensure UTC
  const daysSinceModifiedEpoch = Math.floor(
    (date.getTime() - MODIFIED_EPOCH) / MS_PER_DAY,
  );
  return daysSinceModifiedEpoch;
}

/**
 * Check if a Unix timestamp would exceed 32-bit signed integer limit
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @return {boolean} True if the date would exceed 32-bit limit when converted to Unix timestamp
 */
function wouldExceed32BitLimit(dateString: string): boolean {
  const timestamp = Math.floor(Date.parse(dateString) / 1000);
  return timestamp > 2147483647; // 32-bit signed int max (Jan 19, 2038)
}

/* ---------- component ---------- */
export function FdFarmForm(props: {
  farm?: IFdFarm;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (farm: Partial<IFdFarm>) => void;
  cancelButtonTitle?: string;
  onCancel?: () => void;
  readonly?: boolean;
}) {
  /* local shape (string leaseEndDate) */
  type Submission = {
    name: string;
    description?: string;
    numberOfLeasedHectares?: number;
    ownedHectares?: number;
    irrigatedHectares?: number;
    drylandHectares?: number;
    isLeased?: boolean;
    leaseEndDate?: string; // string while in the form
    waterRights?: boolean;
    fullTimeFarming?: boolean;
    shortTermGoals?: string;
    longTermGoals?: string;
    landComposition?: string;
    workforceNumber?: number;
    workerTrust?: string;
    employmentBeforeAndAfter?: string;
  };

  const {handleSubmit, control, setValue} = useForm<Partial<Submission>>({
    defaultValues: {
      ...props.farm,
      leaseEndDate: toDateInputValue(props.farm?.leaseEndDate),
      fullTimeFarming: props.farm?.fullTimeFarming ?? false,
    },
  });

  const isLeased = useWatch({control, name: 'isLeased'});
  const numberOfLeasedHectares = useWatch({
    control,
    name: 'numberOfLeasedHectares',
  });

  // Auto-calculate isLeased based on numberOfLeasedHectares
  useEffect(() => {
    if (
      numberOfLeasedHectares !== undefined &&
      numberOfLeasedHectares !== null
    ) {
      setValue('isLeased', numberOfLeasedHectares > 0);
    }
  }, [numberOfLeasedHectares, setValue]);

  /* --- convert leaseEndDate to number before emitting --- */
  const onSubmit = (data: Partial<Submission>) => {
    let leaseEnd: number | undefined;

    if (data.leaseEndDate && data.leaseEndDate !== '') {
      // Check if date would exceed 32-bit limit (Year 2038 Problem)
      if (wouldExceed32BitLimit(data.leaseEndDate)) {
        // Use modified epoch encoding for dates beyond 2038
        // This stores days since 2000-01-01 instead of Unix timestamp
        leaseEnd = dateToModifiedDays(data.leaseEndDate);
        console.log(
          `Year 2038 workaround: Date ${data.leaseEndDate} encoded as ${leaseEnd} days since 2000-01-01`,
        );
      } else {
        // Use traditional Unix timestamp for dates before 2038 (backward compatibility)
        leaseEnd = Math.floor(Date.parse(data.leaseEndDate) / 1000);
      }
    }

    const payload: Partial<IFdFarm> = {
      ...(data as Omit<Partial<Submission>, 'leaseEndDate'>),
      leaseEndDate: leaseEnd,
      fullTimeFarming: data.fullTimeFarming,
    } as any; // cast so we can include extra fields

    props.onSubmit(payload);
  };

  return (
    <div className="generic-form-container fd-farm-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            {/* ───────── Farm Info ───────── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Farm Information
              </Typography>
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{required: 'Farm name is required'}}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    label="Farm Name"
                    variant="outlined"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    rowsMax={10}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* ───────── Land Details ───────── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Land Details
              </Typography>
            </Grid>

            {/* numeric hectarage fields */}
            {(
              [
                {name: 'numberOfLeasedHectares', label: 'Leased Hectares'},
                {name: 'ownedHectares', label: 'Owned Hectares'},
                {name: 'irrigatedHectares', label: 'Irrigated Hectares'},
                {name: 'drylandHectares', label: 'Dryland Hectares'},
              ] as const
            ).map(({name, label}) => (
              <Grid item xs={6} key={name}>
                <Controller
                  name={name}
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <TextField
                      {...field}
                      label={label}
                      variant="outlined"
                      fullWidth
                      type="number"
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value))
                      }
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
              </Grid>
            ))}

            {/* Is Leased? */}
            <Grid item xs={12}>
              <Controller
                name="isLeased"
                control={control}
                render={({field}) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        {...field}
                        checked={field.value || false}
                        disabled={props.readonly || props.isLoading}
                      />
                    }
                    label="Is Leased?"
                  />
                )}
              />
            </Grid>

            {/* Lease End */}
            {isLeased && (
              <Grid item xs={12}>
                <Controller
                  name="leaseEndDate"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <TextField
                      {...field}
                      label="Lease End Date"
                      variant="outlined"
                      fullWidth
                      type="date"
                      InputLabelProps={{shrink: true}}
                      error={!!error}
                      helperText={error?.message}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Water Rights & Full-time */}
            {(
              [
                {name: 'waterRights', label: 'Water Rights'},
                {name: 'fullTimeFarming', label: 'Full-time Farmer'},
              ] as const
            ).map(({name, label}) => (
              <Grid item xs={12} key={name}>
                <Controller
                  name={name}
                  control={control}
                  render={({field}) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          {...field}
                          checked={field.value || false}
                          disabled={props.readonly || props.isLoading}
                        />
                      }
                      label={label}
                    />
                  )}
                />
              </Grid>
            ))}

            {/* Long-text fields */}
            {(
              [
                {name: 'shortTermGoals', label: 'Short-term Goals'},
                {name: 'longTermGoals', label: 'Long-term Goals'},
                {name: 'landComposition', label: 'Land Composition'},
              ] as const
            ).map(({name, label}) => (
              <Grid item xs={12} key={name}>
                <Controller
                  name={name}
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      label={label}
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      rowsMax={10}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
              </Grid>
            ))}

            {/* Workforce & trust */}
            <Grid item xs={6}>
              <Controller
                name="workforceNumber"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    label="Workforce Number"
                    variant="outlined"
                    fullWidth
                    type="number"
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                    error={!!error}
                    helperText={error?.message}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="workerTrust"
                control={control}
                render={({field}) => (
                  <TextField
                    {...field}
                    label="Worker Trust"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    rowsMax={10}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>

            {/* Employment Before & After */}
            <Grid item xs={12}>
              <Controller
                name="employmentBeforeAndAfter"
                control={control}
                render={({field}) => (
                  <TextField
                    {...field}
                    label="Employment Before & After"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    rowsMax={10}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* -------- action buttons -------- */}
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              {props.cancelButtonTitle && (
                <Grid item xs={12} sm={6}>
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={props.cancelButtonTitle ? 6 : 12}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
